import moment from "moment";

export const reportPeriod = (values, now = moment()) => {
  const mode = String(values.period || "12");
  let start;
  let end;
  if (mode === "custom" || mode === "All") {
    const range = values[mode === "custom" ? "rangeMonth" : "allRangeMonth"] || [];
    start = range[0] ? moment(range[0]) : moment.invalid();
    end = range[1] ? moment(range[1]) : moment.invalid();
  } else if (mode === "YTD") {
    end = now.clone();
    start = now.clone().startOf("year");
  } else {
    end = values.endingMonth ? moment(values.endingMonth) : moment.invalid();
    const count = Number(mode);
    if (!Number.isInteger(count) || count < 1 || count > 120) throw new Error("Choose a valid report period.");
    start = end.clone().subtract(count - 1, "months");
  }
  if (!start.isValid() || !end.isValid() || start.isAfter(end, "month")) {
    throw new Error("Choose a reporting period in Report Builder before generating the summary.");
  }
  return { start: start.startOf("month").format("YYYY-MM-DD"), end: end.endOf("month").format("YYYY-MM-DD") };
};

export const reportDraftScope = (values) => JSON.stringify({
  ...reportPeriod(values), audience: values.reportAudience || "Facility leadership",
});

export const reportDraftIsStale = (values) => {
  if (!values.managementSummary || !values.aiReportScope) return false;
  try { return values.aiReportScope !== reportDraftScope(values); } catch { return true; }
};

export const serializeReportForm = (values) => {
  const result = { ...values };
  for (const key of ["endingMonth", "rangeMonth", "allRangeMonth"]) {
    const format = (value) => moment.isMoment(value) ? value.format("YYYY-MM-DD") : value;
    result[key] = Array.isArray(values[key]) ? values[key].map(format) : format(values[key]);
  }
  return result;
};

export const buildReportDraftPrompt = (values) => {
  const period = reportPeriod(values);
  return `Draft a management report for this facility now.
Reporting period: ${period.start} through ${period.end}, inclusive.
Audience: ${values.reportAudience || "Facility leadership"}.
The reporting period and audience are already specified; do not ask for them again.
Use authorized utility (electricity, natural gas and water where available), work-order and equipment evidence for this reporting period. Also use the unified facility activity feed for recorded changes across other connected modules, including projects, tasks, preventive maintenance, service requests, contracts and functional tests when available. Count activity entries as events, not distinct jobs or completed outcomes. For activity queries, cap the end date at the facility business date when the reporting period ends in the future; split ranges longer than 366 days. Label this partial-period cutoff explicitly.
Cover key changes, outstanding issues, and next priorities. Use a comparable prior period where available for changes and identify that baseline. Clearly label current equipment or open-work snapshots separately from historical reporting-period facts. Disclose missing sources and partial coverage instead of inventing results.
Return an editable report narrative with inline source citations and completeness limits, suitable for inclusion before the report's charts. Use short paragraphs with clear section headings, avoid Markdown tables, and distinguish evidence from recommendations. Do not return a clarification or a generic count in place of the report. Do not claim that saving or exporting has occurred.`;
};

// Only a final, successful server answer can become report content. Token
// deltas and a transport close are never treated as a completed report.
export const generateReportDraft = async ({ stream, values, onProgress, ...request }) => {
  let answer = "";
  let manifest = null;
  let error = "";
  await stream({ ...request, message: buildReportDraftPrompt(values), onEvent: (event) => {
    if (event.event === "progress") onProgress?.(event.summary || "Gathering report evidence…");
    if (event.event === "error" || event.error || event.message?.status === "error") {
      error = "The report summary could not be completed. Please retry.";
    }
    if (event.event === "answer_done" || event.event === "done") {
      answer = event.message?.content_markdown || event.answer || answer;
      manifest = event.message?.response_manifest || event.response_manifest || manifest;
    }
  } });
  if (error) throw new Error(error);
  const citations = manifest?.citations || [];
  if (!answer.trim() || !citations.length) throw new Error("No sourced report summary was returned. Please retry or adjust the report period.");
  const sources = citations.map((citation) => {
    const periods = (citation.periods || []).map((period) => [period.start, period.end].filter(Boolean).join(" to ")).filter(Boolean).join("; ");
    return `[${String(citation.id).replace("cit-", "")}] ${citation.label}${periods ? ` (${periods})` : ""}`;
  }).join("\n");
  return {
    managementSummary: answer.replace(/\[(\d+)\]\(#citation-cit-\d+\)/g, "[$1]").replace(/^#{1,6}\s+/gm, "").replace(/\*\*([^*]+)\*\*/g, "$1"),
    managementSummarySources: sources,
    aiReportScope: reportDraftScope(values),
  };
};
